import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listOnboardingActivity, resendOnboardingConfirmation } from "@/lib/applications.functions";
import { getIsAdmin } from "@/lib/payments.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/admin/onboarding-activity/$userId")({
  head: () => ({ meta: [{ title: "Onboarding Activity — Admin" }] }),
  component: AdminOnboardingActivity,
});

type EventRow = {
  id: string;
  event_type: string;
  actor_user_id: string | null;
  actor_role: string | null;
  actor_name: string | null;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

type ProfileRow = {
  id: string;
  role: string | null;
  full_name: string | null;
  business_name: string | null;
  headline: string | null;
  business_email: string | null;
  phone_number: string | null;
  address: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  postcode: string | null;
  country: string | null;
  address_verified_at: string | null;
  onboarding_status: string | null;
  venue_review_decision: string | null;
  venue_review_notes: string | null;
  venue_reviewed_at: string | null;
  cv_reviewed_at: string | null;
  venue_onboarding_completed_at: string | null;
  bartender_onboarding_completed_at: string | null;
  created_at: string;
  email: string | null;
};

const EVENT_LABEL: Record<string, { label: string; tone: "neutral" | "good" | "bad" | "info" }> = {
  onboarding_submitted: { label: "Onboarding submitted", tone: "info" },
  onboarding_edited:    { label: "Details edited",       tone: "neutral" },
  approved:             { label: "Approved",             tone: "good" },
  rejected:             { label: "Rejected",             tone: "bad" },
  confirmation_email_resent: { label: "Confirmation email resent", tone: "neutral" },
  admin_note:           { label: "Admin note",           tone: "info" },
};

function AdminOnboardingActivity() {
  const { userId: subjectId } = Route.useParams();
  const navigate = useNavigate();
  const checkAdmin = useServerFn(getIsAdmin);
  const loadActivity = useServerFn(listOnboardingActivity);
  const resend = useServerFn(resendOnboardingConfirmation);

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [resending, setResending] = useState(false);

  const refresh = async () => {
    setLoading(true);
    const r = await loadActivity({ data: { subject_user_id: subjectId } });
    if ("error" in r) toast.error(r.error);
    else {
      setProfile(r.profile as ProfileRow);
      setEvents(r.events as EventRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      const a = await checkAdmin();
      if (!a.isAdmin) { navigate({ to: "/dashboard", replace: true }); return; }
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId]);

  const handleResend = async () => {
    setResending(true);
    try {
      const r = await resend({ data: { subject_user_id: subjectId } });
      if ("error" in r) throw new Error(r.error);
      toast.success("Confirmation email re-queued.");
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not resend.");
    } finally {
      setResending(false);
    }
  };

  const displayName =
    profile?.business_name || profile?.headline || profile?.full_name || "Unnamed";
  const fullAddress = profile
    ? [profile.address_line1, profile.address_line2, profile.city, profile.postcode]
        .filter(Boolean).join(", ") || profile.address || "—"
    : "—";
  const canResend = Boolean(
    profile && (
      (profile.role === "venue" && profile.venue_onboarding_completed_at) ||
      (profile.role === "bartender" && profile.bartender_onboarding_completed_at)
    ),
  );

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-4xl px-6 py-10">
        <Link to="/admin" className="text-xs tracking-wide-luxury text-accent hover:text-accent/80">
          ← Back to admin console
        </Link>
        <h1 className="mt-4 font-display text-3xl">Onboarding Activity</h1>
        <span className="gold-divider mt-3" />

        {loading ? (
          <p className="mt-10 text-sm text-muted-foreground">Loading…</p>
        ) : !profile ? (
          <p className="mt-10 text-sm text-muted-foreground">Profile not found.</p>
        ) : (
          <>
            <section className="mt-8 border border-border bg-card p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-1">
                  <p className="text-xs tracking-wide-luxury text-muted-foreground capitalize">{profile.role}</p>
                  <p className="font-display text-2xl">{displayName}</p>
                  <p className="text-xs text-muted-foreground">Contact: {profile.full_name || "—"}</p>
                  <p className="text-xs text-muted-foreground">Login: {profile.email || "—"}</p>
                  {profile.business_email && (
                    <p className="text-xs text-muted-foreground">Business email: {profile.business_email}</p>
                  )}
                  <p className="text-xs text-muted-foreground">Phone: {profile.phone_number || "—"}</p>
                  <p className="text-xs text-muted-foreground">
                    Address: {fullAddress}
                    {profile.address_verified_at && (
                      <span className="ml-2 text-accent">· verified</span>
                    )}
                  </p>
                  <p className="mt-2 text-xs tracking-wide-luxury text-muted-foreground">
                    Status: <span className="text-foreground">{profile.onboarding_status ?? "—"}</span>
                  </p>
                </div>
                <Button
                  onClick={handleResend}
                  disabled={!canResend || resending}
                  className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury"
                >
                  {resending ? "Resending…" : "Resend confirmation email"}
                </Button>
              </div>
              {!canResend && (
                <p className="mt-3 text-xs text-muted-foreground">
                  Resend becomes available once the user finishes the onboarding wizard.
                </p>
              )}
            </section>

            <section className="mt-8">
              <h2 className="font-display text-xl">Timeline</h2>
              {events.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">No activity recorded yet.</p>
              ) : (
                <ol className="mt-5 relative border-l border-border pl-6 space-y-6">
                  {events.map((e) => {
                    const meta = EVENT_LABEL[e.event_type] ?? { label: e.event_type, tone: "neutral" as const };
                    const dot =
                      meta.tone === "good" ? "bg-accent" :
                      meta.tone === "bad"  ? "bg-destructive" :
                      meta.tone === "info" ? "bg-foreground" : "bg-muted-foreground";
                    return (
                      <li key={e.id} className="relative">
                        <span className={`absolute -left-[31px] top-1.5 h-2.5 w-2.5 rounded-full ${dot}`} />
                        <p className="text-xs tracking-wide-luxury text-muted-foreground">
                          {new Date(e.created_at).toLocaleString()} · {e.actor_role ?? "system"}
                          {e.actor_name ? ` (${e.actor_name})` : ""}
                        </p>
                        <p className="mt-1 font-display text-base">{meta.label}</p>
                        {e.notes && (
                          <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{e.notes}</p>
                        )}
                        {e.metadata && Object.keys(e.metadata).length > 0 && (
                          <pre className="mt-2 overflow-x-auto rounded border border-border bg-muted/20 p-2 text-[11px] text-muted-foreground">
{JSON.stringify(e.metadata, null, 2)}
                          </pre>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
